import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Check,
  CalendarDays,
  DollarSign,
  TrendingUp,
  Users,
  Target,
  Quote,
  ShieldCheck,
  Building2,
  Sparkles,
  X,
} from "lucide-react";

const CALENDLY = "https://calendly.com/jonathan-0rgp/fabg-intro-call";
const REGISTER_URL = "https://fullarch360.io/register/personal";
const IMG_FOUNDER = "/founder-jonathan-theis.jpg";
const IMG_HERO =
  "https://images.pexels.com/photos/5355726/pexels-photo-5355726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000";

const NAV_ITEMS = [
  { id: "problem", label: "The Problem" },
  { id: "pillars", label: "What You Get" },
  { id: "savings", label: "Savings" },
  { id: "vendors", label: "Vendors" },
  { id: "testimonials", label: "Reviews" },
];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const Kicker = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
    {children}
  </p>
);

const PrimaryCta = ({ testid, onClick, children = "Start Your Free Trial", className = "" }) => (
  <a
    href={REGISTER_URL}
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground shadow-sm hover:-translate-y-px hover:shadow-md transition-all duration-200 active:scale-[0.98] ${className}`}
  >
    {children}
    <ArrowRight className="ml-2 h-5 w-5" />
  </a>
);

const CallCta = ({ testid, className = "" }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border bg-white px-6 text-base font-medium text-slate-700 hover:bg-secondary transition-colors duration-200 ${className}`}
  >
    <CalendarDays className="h-5 w-5 text-primary" />
    Book a 15-Minute Intro Call
  </a>
);

export default function UsaLandingPage() {
  useEffect(() => {
    document.title = "FABG USA — The Full Arch Buying Group | 60-Day Free Trial";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "Access DSO-level buying power, business coaching, community, and growth resources for implant & full arch practices — without selling equity. Try FABG free for 60 days.",
      );
  }, []);

  const trackLead = () => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead");
    }
  };

  const goToRegister = () => {
    trackLead();
    window.location.href = REGISTER_URL;
  };

  const heroBullets = [
    "Implants as low as $45",
    "MUAs as low as $22",
    "Full Arch Zirconia as low as $1,000 Start-to-Finish",
    "Complimentary 1-on-1 Full Arch Business Coaching",
    "Private Community of Implant & Full Arch Practice Owners",
  ];

  const problems = [
    "Rising implant and restorative costs",
    "Expensive marketing with inconsistent results",
    "Limited leverage with vendors",
    "Difficulty increasing full arch volume",
    "Lack of trusted business resources",
    "No community of like-minded operators",
    "Little guidance on how to grow profitably",
  ];

  const pillars = [
    {
      icon: DollarSign,
      title: "Save Money",
      intro: "Access negotiated pricing from trusted vendors. Examples include:",
      bullets: [
        "Implants as low as $45",
        "MUAs as low as $22",
        "Full Arch Zirconia as low as $1,000",
        "Implant Restorations w/ Custom Abutments as low as $250",
        "Zirconia Crowns as low as $59",
        "Savings on biologics, equipment, software & supplies",
      ],
    },
    {
      icon: TrendingUp,
      title: "Grow Your Practice",
      intro: "The goal isn\u2019t just to cut costs \u2014 it\u2019s to increase production and profitability. Gain access to:",
      bullets: [
        "Full Arch Marketing Resources",
        "Case Acceptance Tools",
        "KPI Tracking & Sales Systems",
        "Practice Growth Strategies",
        "Webinar Series",
        "Business Resources",
      ],
    },
    {
      icon: Users,
      title: "Community",
      intro: "Join a private network of growth-minded owners. Learn from doctors actively:",
      bullets: [
        "Growing full arch practices",
        "Implementing new technology",
        "Improving workflows",
        "Testing marketing strategies",
        "Increasing profitability",
        "Sharing what\u2019s actually working",
      ],
    },
    {
      icon: Target,
      title: "Expert Guidance",
      intro: "Every membership includes complimentary 1-on-1 Full Arch Business Coaching. Topics include:",
      bullets: [
        "Full Arch Growth",
        "Marketing",
        "Case Acceptance",
        "Team Development",
        "Digital Workflows",
        "Profitability & Practice Expansion",
      ],
    },
  ];

  const vendors = [
    "Neodent",
    "SIN 360",
    "MegaGen",
    "J Dental Care",
    "JB Dental Lab",
    "Power Dental Studio",
    "Integrity Dental Lab",
    "Maxxeus",
    "Zest",
    "ASI",
    "Versah",
    "Voxel Dental Solutions",
    "DDS Marketing",
    "Young Surgical",
    "Preat",
    "Sonrie.AI",
  ];

  const testimonials = [
    {
      quote:
        "I just had a 1-hour call with Jonathan today and learned a lot about what I can be doing better in my office to hit my goals. Definitely recommend it.",
      name: "Dr. Alessandro Retis",
      role: "Coaching",
    },
    {
      quote:
        "Just had a call with Jonathan Theis, the full arch coach. Can\u2019t speak enough about his mastery of management and implementation \u2014 the man is dropping pearls that are team-based and performance-based. 10/10 would recommend!!",
      name: "Dr. Alex Shore",
      role: "Coaching",
    },
    {
      quote:
        "Spending time with Dr. Theis exceeded my expectations. He brings an incredible depth of knowledge and a genuine passion for teaching and mentorship. Our conversation went far beyond clinical dentistry and focused heavily on the business side of running and growing a practice. I look forward to continuing to learn from him as part of FABG.",
      name: "Dr. Jonathan Bensoussan",
      role: "Prosthodontist",
    },
    {
      quote:
        "Really enjoyed the coaching session \u2014 there was a lot of practical, real-world value that I can immediately apply in my practice. It helped me think differently about both clinical and business decisions. Definitely a worthwhile part of the membership.",
      name: "Dr. Husnain Shahid",
      role: "Coaching",
    },
    {
      quote:
        "My coaching sessions with Dr. Jonathan Theis have been great!! Running a Full Arch practice that also does general, cosmetic and hygiene is challenging. His expertise and insight have been invaluable \u2014 it\u2019s comforting knowing he\u2019s \u2018been there and done that\u2019 as a doctor-owner.",
      name: "Dr. Sridhar Sista",
      role: "Coaching",
    },
    {
      quote:
        "My session with Dr. Theis was really informative and valuable. We worked through current issues in the practice and game-planned for future ones. He always leaves you with action items to ensure progress \u2014 truly helpful with immediate impact. Highly recommend.",
      name: "Omar Yousuf",
      role: "Coaching",
    },
    {
      quote:
        "I joined the Full Arch Buying Group because I heard they had great deals on implants. I realized what Dr. Theis is building is more than that \u2014 a great community where like-minded dentists learn. He knows step by step how to scale a full arch practice. Extremely grateful for the time I have with him!",
      name: "Dr. Clifford Young",
      role: "Community",
    },
    {
      quote:
        "Thanks for the 1-on-1 today \u2014 it\u2019s wonderful to have you as a resource as we enter this journey. Having \u2018been there and done that\u2019 gives us confidence in your advice, and it\u2019s useful that you have a verified network for us to work with. The advice was precise and concise.",
      name: "Rael Bernstein, DDS MS",
      role: "Growth",
    },
  ];

  const whyStay = [
    "Community",
    "Coaching",
    "Business Resources",
    "Trusted Vendor Relationships",
    "Shared Knowledge",
    "Accountability",
    "Access to What Is Actually Working",
  ];

  const whoFor = [
    "GPs placing implants",
    "Full Arch Providers",
    "Implant Centers",
    "Oral Surgeons",
    "Periodontists",
    "Multi-Practice Owners",
    "Doctors looking to grow Full Arch production",
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header
        onApply={goToRegister}
        navItems={NAV_ITEMS}
        ctaLabel="Start Free Trial"
        ctaShort="Free Trial"
        secondaryCta={{ label: "Book a Call", href: CALENDLY }}
      />

      {/* HERO */}
      <section className="hero-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Badge className="rounded-full bg-accent text-primary hover:bg-accent border-0">
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  Try FABG Free for 60 Days
                </Badge>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.4rem] font-semibold tracking-tight text-foreground leading-[1.05]">
                  Stop Building Your Full Arch Practice{" "}
                  <span className="text-primary">Alone</span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Access DSO-level buying power, business coaching, community,
                  and growth resources &mdash; without selling equity. Built by
                  Full Arch Operators, for Full Arch Operators.
                </p>
              </Reveal>

              <Reveal delay={0.15}>
                <ul className="mt-6 space-y-2.5">
                  {heroBullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-sm sm:text-base text-slate-700">{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <PrimaryCta onClick={trackLead} testid="hero-start-trial-button" />
                  <CallCta testid="hero-book-call-button" />
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-4 text-sm font-medium text-slate-500">
                  No Contracts &bull; Cancel Anytime &bull; No Risk
                </p>
              </Reveal>
            </div>

            <div className="mt-10 lg:mt-0 lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative rounded-3xl bg-primary/5 p-2">
                  <img
                    src={IMG_HERO}
                    alt="Full arch implant dentist with a patient in a modern practice"
                    className="w-full h-72 sm:h-96 lg:h-[460px] object-cover rounded-2xl border border-border shadow-sm"
                    loading="eager"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section id="problem" className="bg-white border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>The Problem</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Most Full Arch Practices Are Leaving Money on the Table
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <Reveal className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {problems.map((p) => (
                  <div
                    key={p}
                    className="flex items-start gap-3 rounded-xl border border-border bg-secondary/50 p-4"
                  >
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-destructive/10 text-destructive">
                      <X className="h-3 w-3" />
                    </span>
                    <span className="text-sm text-slate-700">{p}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="rounded-2xl border border-border bg-white shadow-sm p-6 relative overflow-hidden">
                <span className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
                <p className="text-base text-slate-700 leading-relaxed">
                  Most doctors are trying to solve these challenges{" "}
                  <span className="font-semibold">alone.</span>
                </p>
                <p className="mt-4 text-lg font-semibold text-foreground">
                  FABG was built to change that.
                </p>
                <PrimaryCta
                  onClick={trackLead}
                  testid="problem-start-trial-button"
                  className="mt-6 w-full sm:w-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT IS FABG */}
      <section id="about" className="section-accent border-t border-border scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <Reveal>
            <Kicker>What Is FABG?</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              More Than a Buying Group
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                "Most buying groups focus on discounts.",
                "Most consultants focus on strategy.",
                "Most online communities focus on discussion.",
              ].map((t) => (
                <div
                  key={t}
                  className="rounded-xl border border-border bg-white p-5 text-sm text-slate-600"
                >
                  {t}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg font-semibold text-primary">
              FABG combines all three.
            </p>
            <p className="mt-4 mx-auto max-w-2xl text-base text-slate-700 leading-relaxed">
              Our mission is simple: help implant and full arch practice owners
              increase profitability, grow their practices, and stay on the
              cutting edge of what&rsquo;s working.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE FOUR PILLARS */}
      <section id="pillars" className="bg-white border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>The Four Pillars</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Everything you get inside FABG
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-white shadow-sm p-6 transition-shadow duration-200 hover:shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent">
                      <p.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {p.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                    {p.intro}
                  </p>
                  <ul className="mt-4 space-y-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 flex-none text-primary" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REAL MEMBER SAVINGS */}
      <section id="savings" className="border-t border-border bg-foreground scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-primary-foreground/70">
                Real Member Savings
              </p>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-3xl mx-auto">
                What Would Better Pricing Mean For Your Practice?
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-sm text-slate-300">If your practice completes</p>
                <p className="mt-2 font-display text-3xl font-semibold text-white">
                  10 arches
                </p>
                <p className="text-sm text-slate-300">per month</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-sm text-slate-300">And saves</p>
                <p className="mt-2 font-display text-3xl font-semibold text-white">
                  $800&ndash;$1,500
                </p>
                <p className="text-sm text-slate-300">per case</p>
              </div>
              <div className="rounded-2xl border border-primary/40 bg-primary/20 p-6 text-center">
                <p className="text-sm text-primary-foreground/80">That&rsquo;s</p>
                <p className="mt-2 font-display text-3xl font-semibold text-white">
                  $96k&ndash;$180k
                </p>
                <p className="text-sm text-primary-foreground/80">per year in margin</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> Without increasing production
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> Without increasing ad spend
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" /> Without adding operatories
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex justify-center">
              <PrimaryCta onClick={trackLead} testid="savings-start-trial-button" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUSTED PARTNERS */}
      <section id="vendors" className="bg-white border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Trusted Partners</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Vendor Relationships You Can Leverage Immediately
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {vendors.map((v) => (
                <div
                  key={v}
                  className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-4 text-sm font-medium text-slate-700"
                >
                  <Building2 className="h-4 w-4 flex-none text-primary/70" />
                  {v}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-border bg-accent/50 p-6">
              <ShieldCheck className="h-8 w-8 flex-none text-primary" />
              <p className="text-base font-medium text-foreground">
                75+ vetted vendors across virtually every category a full arch
                practice spends money.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        id="testimonials"
        data-testid="usa-testimonials-section"
        className="section-accent border-t border-border scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Testimonials</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              What Members Are Saying
            </h2>
          </Reveal>
          <div className="mt-10 columns-1 md:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="mb-5 break-inside-avoid rounded-2xl border border-border bg-white shadow-sm p-6"
              >
                <Quote className="h-6 w-6 text-primary/30" />
                <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                  {t.quote}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                  <div className="flex gap-0.5 text-primary">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Sparkles key={s} className="h-3.5 w-3.5" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DOCTORS JOIN */}
      <section className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:col-span-5">
              <Kicker>Why Doctors Join</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Most members join for pricing.
                <span className="text-primary"> They stay for everything else.</span>
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Members consistently tell us the biggest long-term value comes
                from what surrounds the savings.
              </p>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {whyStay.map((w) => (
                  <div
                    key={w}
                    className="flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-4"
                  >
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium text-slate-700">{w}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO IS FABG FOR */}
      <section id="who" className="section-accent border-t border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Reveal>
            <Kicker>Who Is FABG For?</Kicker>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground max-w-2xl">
              Built For Growth-Minded Implant Dentists
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 rounded-2xl border border-border bg-white p-6 sm:p-8 shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whoFor.map((w) => (
                  <div key={w} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm sm:text-base text-slate-700">{w}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-base text-slate-700">
                Whether you&rsquo;re placing 2 arches a month or 100, the goal is
                the same:{" "}
                <span className="font-semibold text-foreground">
                  grow smarter, increase profitability, and stay ahead.
                </span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT JONATHAN */}
      <section className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:col-span-4">
              <div className="rounded-2xl border border-border bg-white shadow-sm p-6 text-center">
                <div className="mx-auto h-32 w-32 overflow-hidden rounded-full border-4 border-accent shadow-sm">
                  <img
                    src={IMG_FOUNDER}
                    alt="Dr. Jonathan Theis, Founder of the Full Arch Buying Group"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="mt-5 text-lg font-semibold text-foreground">
                  Dr. Jonathan Theis
                </p>
                <p className="text-sm text-muted-foreground">
                  Founder, Full Arch Buying Group
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-8">
              <Kicker>About Jonathan</Kicker>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                Built By Someone Who Has Been In Your Shoes
              </h2>
              <p className="mt-4 text-base text-slate-700 leading-relaxed">
                Dr. Jonathan Theis is a full arch dentist, consultant, practice
                owner, and founder of the Full Arch Buying Group. After years of
                building implant practices, negotiating vendor relationships, and
                helping doctors improve profitability, he created FABG to give
                private practice owners access to the same advantages typically
                reserved for larger organizations.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border bg-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              What Would One More Full Arch Case Per Month Mean To Your Practice?
            </h2>
            <p className="mt-5 mx-auto max-w-2xl text-base text-slate-300 leading-relaxed">
              What would better pricing mean? Better systems? Better guidance?
              You don&rsquo;t have to figure it out alone.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-display text-xl font-semibold text-white">
              Start Your 60-Day Free Trial Today
            </p>
            <p className="mt-1 text-sm text-slate-400">
              No Contracts. Cancel Anytime. Nothing To Lose.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <PrimaryCta onClick={trackLead} testid="final-start-trial-button" />
              <CallCta testid="final-book-call-button" />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer
        tagline="The Full Arch Buying Group"
        disclaimer="Pricing examples shown in USD from the FABG network. Actual pricing varies by vendor and volume."
      />
    </div>
  );
}
